import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import { Paper, Menu, Grid } from '@material-ui/core'
import { GraphHeading, TileStyle, StyledMenuItem, StyledRightButton, StyledMenuHeader, colors } from '../../theme/Global'
import Choropleth, {
  getFillByCount,
  getStrokeByCount,
  GEO_MAP,
  getDomainBy
} from '../../common/components/drawMap'
import Planograms from './Planograms'
import { usStates, usTerritories, caProvinces } from '../../constants/localeNames'
import postingsUpdateService from '../../services/postingsUpdatesService'
import { StyledCountryButton, StyledCountrySpan, StyledDropDownIcon } from './styles'
import { getLocaleFacets } from './PostingsLocations/services/postingsLocationsService'
import { IppLink } from './IppLink/IppLink'

const LocationMap = ({ companyId, ipp, history }) => {
  const [countries, setCountries] = useState([])
  const [data, setData] = useState([{}])
  const [loading, setLoading] = useState(true)
  const [selectedCountry, setSelectedCountry] = useState(GEO_MAP.US)
  const [countryMenuAnchor, setCountryMenuAnchor] = useState(null)
  const [locationMenuAnchor, setLocationMenuAnchor] = useState(null)
  const [planograms, setPlanograms] = useState([])

  const fetchPlanograms = async () => {
    try {
      const planogramReturn = await postingsUpdateService.getPlanograms({ planogramsOnly: true })
      setPlanograms(planogramReturn.planograms)
    } catch (error) {
      console.error('An error occurred when retrieving planograms')
    }
  }

  const fetchPostingsLocaleFacets = async (subscribed) => {
    setLoading(true)

    try {
      const localeFacetResponse = await getLocaleFacets({ companyId })

      if (localeFacetResponse && subscribed) {
        const validCountryCodes = ['US', 'CA']
        const validTerritoryCodes = ['PR', 'VI', 'GU']
        const foundCountriesTerritories = []
        const locationsCount = {}

        localeFacetResponse.map((entry) => {
          const [countryCode, stateCode] = entry.facet.split('~')

          if (!countryCode || !stateCode) {
            console.error(`Bad data in locationMap from localeFacets: ${JSON.stringify(entry)}`)
          }

          const addCodeIfValid = (code, validCodes) => {
            if (code && validCodes.includes(code) && !foundCountriesTerritories.includes(code)) {
              foundCountriesTerritories.push(code)
            }

            if (locationsCount[code] === undefined) {
              locationsCount[code] = []
            }

            locationsCount[code].push({ stateAbv: stateCode, count: entry.count })
          }

          addCodeIfValid(countryCode, validCountryCodes)
          addCodeIfValid(stateCode, validTerritoryCodes)

          return null
        })

        setCountries(foundCountriesTerritories.sort())
        setData(locationsCount[selectedCountry.code] || [])

        if (!foundCountriesTerritories.includes(selectedCountry.code)) {
          setSelectedCountry(GEO_MAP[foundCountriesTerritories[0]] || GEO_MAP.US)
        }

        setLoading(false)
      }
    } catch (error) {
      setLoading(false)
      console.error('An error occurred when retrieving postings locale facets')
    }

    return null
  }

  const openCountryMenu = (e) => {
    setCountryMenuAnchor(e.currentTarget)
  }

  const closeCountryMenu = (e) => {
    setCountryMenuAnchor(null)
  }

  const openLocationMenu = (e) => {
    setLocationMenuAnchor(e.currentTarget)
  }

  const closeLocationMenu = (e) => {
    setLocationMenuAnchor(null)
  }

  const getCountryCode = (datum) => {
    const prCode = datum.properties.GID_0 === 'PRI' ? 'PR' : null
    const viCode = datum.properties.adm0_a3 === 'VIR' ? 'VI' : null
    const guCode = datum.properties.hasc === 'GU' ? 'GU' : null
    return datum.properties.iso_a2 || prCode || viCode || guCode
  }

  const onMapClick = (event) => {
    const country = getCountryCode(event)
    const state = event.properties.postal ? event.properties.postal : country
    history.push(`/compliance/locations?state=${state}&country=${country}`)
  }

  const drawTooltip = (mapData, datum) => {
    const adminLv2Code = datum.properties.postal || getCountryCode(datum)
    const toolTipData = mapData.find((x) => x.stateAbv === adminLv2Code)
    const count = toolTipData ? toolTipData.count : 0
    const name = datum.properties.name
    return `<p style="margin-top: 0px; font-weight: 600;">${name}</p>
      <p style="margin-bottom: 0px">${count} Location${count !== 1 ? 's' : ''}</p>`
  }

  const createMenuItem = (location) => {
    return (
      <StyledMenuItem
        key={location.name}
        country={location.localeRegion}
        onClick={() => {
          closeLocationMenu()
          history.push(`/compliance/locations?state=${location.localeID}&country=${location.localeRegion}`)
        }}
      >
        {location.name}
      </StyledMenuItem>
    )
  }

  useEffect(() => {
    let subscribed = true

    fetchPlanograms()
    fetchPostingsLocaleFacets(subscribed)

    return () => {
      subscribed = false
    }
  }, [selectedCountry])

  return (
    <React.Fragment>
      <Paper style={TileStyle}>
        <GraphHeading>
          {countries.length > 1
            ? <StyledCountryButton disabled={loading} onClick={openCountryMenu}><StyledDropDownIcon />{selectedCountry.name} Locations</StyledCountryButton>
            : <StyledCountrySpan>{selectedCountry.name} Locations</StyledCountrySpan>}
          <Menu
            anchorEl={countryMenuAnchor}
            open={Boolean(countryMenuAnchor)}
            onClose={closeCountryMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            getContentAnchorEl={null}
          >
            {countries.map((item) => (
              <StyledMenuItem
                key={GEO_MAP[item].name}
                country={GEO_MAP[item]}
                onClick={() => {
                  closeCountryMenu()
                  setSelectedCountry(GEO_MAP[item])
                }}
              >
                {GEO_MAP[item].name}
              </StyledMenuItem>
            ))}
          </Menu>
          <StyledRightButton id="select-location-button" disabled={loading} onClick={openLocationMenu}>Select Location</StyledRightButton>
          <Menu
            anchorEl={locationMenuAnchor}
            open={Boolean(locationMenuAnchor)}
            onClose={closeLocationMenu}
            PaperProps={{
              style: {
                maxHeight: 100 * 4.5,
                width: '25ch'
              }
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            getContentAnchorEl={null}
          >
            {countries.includes('US') && (
              [
                <StyledMenuHeader
                  key="United States"
                  country="US"
                  disabled
                  aria-hidden="true"
                >
                  US States
                </StyledMenuHeader>,
                usStates.map((location) => (
                  createMenuItem(location)
                ))
              ]
            )}
            {(countries.includes('GU') || countries.includes('PR') || countries.includes('VI')) && (
              [
                <StyledMenuHeader
                  key="United States Territories"
                  country="US"
                  disabled
                  aria-hidden="true"
                  style={countries.includes('US') ? { borderTop: `1px solid ${colors.lightGrey}` } : { }}
                >
                  US Territories
                </StyledMenuHeader>,
                usTerritories.map((location) => (
                  createMenuItem(location)
                ))
              ]
            )}
            {countries.includes('CA') && (
              [
                <StyledMenuHeader
                  key="Canada"
                  country="CA"
                  disabled
                  aria-hidden="true"
                  style={countries.length !== 1 ? { borderTop: `1px solid ${colors.lightGrey}` } : { }}
                >
                  Canadian Provinces
                </StyledMenuHeader>,
                caProvinces.map((location) => (
                  createMenuItem(location)
                ))
              ]
            )}
          </Menu>
        </GraphHeading>
        <Choropleth
          ledgendFormat=".1s"
          onMapClick={onMapClick}
          mapData={data}
          country={selectedCountry.code}
          getDomainSpan={getDomainBy(data, 'count')}
          getStrokeForState={getStrokeByCount}
          getFillForState={getFillByCount}
          getTooltipHtml={drawTooltip}
          parentIsLoading={loading}
        />
        <Grid
          container
          justify="space-between"
          style={{ marginTop: '-3.6vw' }}
        >
          <Grid item xs={6}>
            {ipp && ipp.hasAccess && ipp.accessCode && (
              <IppLink ippCode={ipp.accessCode} />
            )}
          </Grid>
          <Grid item xs={6}>
            <Planograms
              country={selectedCountry.code}
              planograms={planograms}
            />
          </Grid>
        </Grid>
      </Paper>
    </React.Fragment>
  )
}

export default withRouter(LocationMap)
